/**
 * bifrostMirrorPipeline — aurora reflected off a mirror surface.
 *
 * A vibrant aurora (multi-color vertical ribbons) in the upper half
 * of the screen, mirrored across a horizontal axis to the lower half.
 * The mirror is a perfectly reflective horizontal line, so the
 * aurora above appears mirrored below, with a slight delay/distortion
 * to suggest water ripples.
 *
 * Innovations:
 *   - The mirror is at y=0. For each pixel above the mirror, the
 *     corresponding pixel below is reflected.
 *   - The aurora ribbons are FBM-modulated vertical strips with
 *     per-ribbon hue. The reflection creates a kaleidoscopic effect
 *     where the upper and lower auroras meet and blend at the mirror.
 *   - Subtle water ripples at the mirror line distort the reflection.
 *   - The mirror line itself is a thin bright gradient.
 *   - Stars in the upper half are also reflected, creating a
 *     symmetrical starfield.
 *
 * Audio:
 *   - bass: aurora intensity
 *   - mid:  ribbon density
 *   - high: ripple intensity at the mirror
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BifrostMirrorPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}

float aurora(vec2 p, float t, float ribbonPhase) {
  float ribbonX = sin(t * 0.3 + ribbonPhase) * 0.4;
  float dist = abs(p.x - ribbonX);
  float width = 0.06 + 0.02 * sin(t * 0.5 + ribbonPhase);
  float core = exp(-dist * dist / (width * width));
  float fbmMod = fbm(vec2(p.x * 2.0, p.y * 3.0 + t * 0.6 + ribbonPhase));
  core *= 0.6 + 0.6 * fbmMod;
  float yFalloff = smoothstep(-0.4, 0.5, p.y);
  return core * yFalloff;
}

vec3 ribbonColor(float fi) {
  vec3 c;
  if (mod(fi, 6.0) < 1.0) c = vec3(0.30, 1.00, 0.55);
  else if (mod(fi, 6.0) < 2.0) c = vec3(0.40, 0.85, 1.00);
  else if (mod(fi, 6.0) < 3.0) c = vec3(0.55, 0.45, 1.00);
  else if (mod(fi, 6.0) < 4.0) c = vec3(1.00, 0.50, 0.85);
  else if (mod(fi, 6.0) < 5.0) c = vec3(1.00, 0.85, 0.45);
  else c = vec3(0.85, 0.95, 1.00);
  return c;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  float r = length(p);
  vec3 bg = mix(
    vec3(0.02, 0.04, 0.10),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Stars in the upper half.
  for (int i = 0; i < 30; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(fi * 1.7 + 0.3) * 1.0,
      cos(fi * 2.1 + 1.7) * 0.45 + 0.20
    );
    float twinkle = 0.5 + 0.5 * sin(uTime * 2.0 + fi);
    float d = length(p - sp);
    col += vec3(0.85, 0.9, 1.0) * exp(-d * 200.0) * twinkle * 0.4;
  }

  float ripple = 0.02 * sin(p.x * 10.0 + uTime * 2.0) * (0.5 + 0.5 * high);

  vec3 auroraColor = vec3(0.0);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float phase = fi * 1.047;
    float a = aurora(p, uTime, phase);
    auroraColor += ribbonColor(fi) * a;
  }
  auroraColor *= (0.6 + 0.8 * bass);

  // Reflection.
  vec2 pRefl = vec2(p.x + ripple, -p.y);
  vec3 auroraRefl = vec3(0.0);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float phase = fi * 1.047;
    float a = aurora(pRefl, uTime, phase);
    auroraRefl += ribbonColor(fi) * a;
  }
  auroraRefl *= (0.6 + 0.8 * bass) * 0.7;
  float lowerMask = step(0.0, -p.y) * step(-0.4, p.y);
  float refAtt = exp(-abs(p.y) * 2.0);
  col += auroraRefl * lowerMask * refAtt;

  // Original aurora (upper half).
  float upperMask = step(0.0, p.y);
  col += auroraColor * upperMask;

  // Mirror line.
  float mirrorLine = exp(-abs(p.y) * 80.0);
  col += vec3(0.7, 0.85, 1.0) * mirrorLine * 0.8;

  // Mirror ripples — small waves.
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    float ripplePhase = fract(uTime * 0.3 + fi * 0.2);
    float rippleDist = abs(p.x - (fi - 2.0) * 0.4 - ripplePhase * 2.0);
    float ripple = exp(-abs(p.y) * 30.0) * exp(-rippleDist * 30.0);
    col += vec3(0.5, 0.8, 1.0) * ripple * 0.3;
  }

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeBifrostMirrorBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeBifrostMirrorRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createBifrostMirrorPipeline(gl: WebGL2RenderingContext): BifrostMirrorPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawBifrostMirror(
  gl: WebGL2RenderingContext,
  pipe: BifrostMirrorPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
